import { Badge, Button, Card, LoadingContainer, Modal, Select } from '@/components/ui';
import { useEliminarEvidenciaSysadmin, useEvidenciasSysadmin } from '@/hooks';
import { useAsesores, useEmpresasSysadmin } from '@/hooks/useSysadmin';
import type { EvidenciaSysadminDTO, EvidenciasFilter } from '@/services/sysadmin.service';
import { FileText, Filter, Trash2 } from 'lucide-react';
import { useState } from 'react';

export function GestionEvidenciasPage() {
  const [filtros, setFiltros] = useState<EvidenciasFilter>({});

  const { data: response, isLoading: loadingEvidencias, error } = useEvidenciasSysadmin(filtros);
  const deleteMutation = useEliminarEvidenciaSysadmin();

  const { empresas, isLoading: loadingEmpresas } = useEmpresasSysadmin();
  const { asesores, isLoading: loadingAsesores } = useAsesores();

  const [evidenceToDelete, setEvidenceToDelete] = useState<EvidenciaSysadminDTO | null>(null);

  const evidencias = response?.evidencias || [];
  const isLoading = loadingEvidencias || loadingEmpresas || loadingAsesores;

  const handleDeleteConfirm = async () => {
    if (!evidenceToDelete) return;
    try {
      await deleteMutation.mutateAsync(evidenceToDelete.evidenciaId);
      alert('Evidencia eliminada correctamente');
      setEvidenceToDelete(null);
    } catch (err: any) {
      alert(err.message || 'Error al eliminar la evidencia');
    }
  };

  const handleClearFilters = () => {
    setFiltros({});
  };

  if (isLoading && evidencias.length === 0) return <LoadingContainer text="Cargando evidencias..." />;

  if (error) {
    return (
      <Card className="p-4 bg-red-50 border-red-100 flex items-center gap-3 text-red-700">
        <p className="text-sm font-medium">Error al cargar evidencias: {(error as Error).message}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Gestión Global de Evidencias
          </h2>
          <p className="text-sm text-gray-500 mt-1">Explora y administra todas las evidencias subidas al sistema.</p>
        </div>
        <Badge variant="blue">{evidencias.length} Registros</Badge>
      </div>

      <div className="flex flex-wrap items-center gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 text-gray-600">
          <Filter className="w-4 h-4" />
          <span className="text-sm font-medium">Filtros:</span>
        </div>

        <div className="w-64">
          <Select
            name="empresaIdFilter"
            value={filtros.empresaId || ''}
            onChange={(e) => setFiltros({ ...filtros, empresaId: e.target.value || undefined })}
            options={[
              { value: '', label: 'Todas las empresas' },
              ...empresas.map((e) => ({ value: e.nitId, label: `${e.nombre} (${e.nitId})` })),
            ]}
          />
        </div>

        <div className="w-64">
          <Select
            name="asesorIdFilter"
            value={filtros.asesorId?.toString() || ''}
            onChange={(e) => setFiltros({ ...filtros, asesorId: e.target.value ? parseInt(e.target.value) : undefined })}
            options={[
              { value: '', label: 'Todos los asesores' },
              ...asesores.map((a) => ({ value: a.asesorId.toString(), label: a.nombre })),
            ]}
          />
        </div>

        {(filtros.empresaId || filtros.asesorId) && (
          <Button variant="secondary" onClick={handleClearFilters} className="text-sm">
            Limpiar filtros
          </Button>
        )}
      </div>

      <Card className="overflow-hidden border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider">Empresa</th>
                <th className="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider">Asesor</th>
                <th className="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider">Archivo</th>
                <th className="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider">Fecha</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600 uppercase tracking-wider">Estado</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-600 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {evidencias.length > 0 ? (
                evidencias.map((evidencia: EvidenciaSysadminDTO) => (
                  <tr key={evidencia.evidenciaId} className="hover:bg-blue-50/30 transition-colors">
                    <td className="px-4 py-3 text-gray-900 font-medium">{evidencia.empresaNombre || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-600">{evidencia.asesorNombre || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900 font-medium truncate max-w-[200px]" title={evidencia.nombre}>
                        {evidencia.nombre}
                      </div>
                      {evidencia.descripcion && (
                        <div className="text-gray-500 text-xs italic truncate max-w-[200px]" title={evidencia.descripcion}>
                          {evidencia.descripcion}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{new Date(evidencia.fechaAdicion).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant={evidencia.estado === 'ACTIVO' ? 'green' : evidencia.estado === 'PENDIENTE' ? 'yellow' : 'red'}>
                        {evidencia.estado}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(evidencia.url, '_blank')}
                          className="hover:text-blue-700 hover:bg-blue-100"
                          title="Ver Archivo"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEvidenceToDelete(evidencia)}
                          className="hover:text-red-700 hover:bg-red-100 text-red-600"
                          title="Eliminar Evidencia"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400 italic">
                    No se encontraron evidencias.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal isOpen={!!evidenceToDelete} onClose={() => setEvidenceToDelete(null)} title="Eliminar Evidencia">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            ¿Estás seguro de que deseas eliminar la evidencia <strong>{evidenceToDelete?.nombre}</strong>?
          </p>
          <div className="p-3 bg-yellow-50 text-yellow-800 text-xs rounded-lg border border-yellow-200">
            <strong>Advertencia:</strong> Esta acción eliminará permanentemente el archivo de nuestro sistema y de Google Drive. No se puede
            deshacer.
          </div>
          <div className="flex gap-3 justify-end pt-4">
            <Button variant="secondary" onClick={() => setEvidenceToDelete(null)} disabled={deleteMutation.isPending}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} isLoading={deleteMutation.isPending}>
              Sí, Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
